import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Search, Lock, RefreshCw, TrendingDown, Radar } from "lucide-react";
import Seo from "../components/Seo";
import FlightCard from "../components/FlightCard";
import TargetPointsControl from "../components/TargetPointsControl";
import { NOTIFY_REFRESH_EVENT } from "../components/NotificationBell";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useWishlist } from "../context/WishlistContext";
import { useToast } from "../hooks/use-toast";
import { api } from "../lib/api";
import { daysFromToday } from "../data/mock";

export default function Wishlist() {
  const { user } = useAuth();
  const { items, refresh } = useWishlist();
  const { toast } = useToast();
  const [checking, setChecking] = useState(false);

  const checkNow = async () => {
    setChecking(true);
    try {
      const res = await api.checkNotifications();
      window.dispatchEvent(new Event(NOTIFY_REFRESH_EVENT));
      await refresh();
      toast({
        title: res.newNotifications > 0 ? `${res.newNotifications} price drop${res.newNotifications > 1 ? "s" : ""} found` : "No new drops",
        description: res.newNotifications > 0 ? "Check the bell in the navbar." : "We’ll keep watching your targets in the background.",
      });
    } catch {
      toast({ title: "Check failed", variant: "destructive" });
    } finally {
      setChecking(false);
    }
  };

  const watching = items.filter((d) => d.targetPoints).length;

  return (
    <main className="min-h-[70vh] bg-slate-50">
      <Seo title="Your saved award deals | FindMyFlight" description="Revisit your favourite award flight deals saved to your FindMyFlight account." path="/wishlist" />
      <div className="mx-auto max-w-6xl px-5 py-10">
        <div className="flex items-center gap-2 text-blue-600"><Heart className="h-5 w-5" /><span className="text-sm font-semibold uppercase tracking-wide">Wishlist</span></div>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900">Your saved deals</h1>
            <p className="mt-2 max-w-2xl text-slate-600">Deals you’ve hearted across Search and Explore. Set a target on any deal and we’ll notify you the moment the route drops below it.</p>
          </div>
          {user && !user.pro && items.length > 0 && (
            <Link to="/pricing" data-testid="wishlist-pro-banner" className="w-full rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 hover:bg-blue-100"><b>Price-drop alerts are a PRO feature.</b> Set targets now — notifications are delivered once you go PRO. <span className="font-bold">Upgrade →</span></Link>
          )}
          {user && items.length > 0 && (
            <div className="flex items-center gap-3">
              <span data-testid="watching-count" className="flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700"><TrendingDown className="h-3.5 w-3.5" /> Watching {watching} route{watching === 1 ? "" : "s"}</span>
              <Button onClick={checkNow} disabled={checking} variant="outline" data-testid="check-prices-button" className="gap-2"><RefreshCw className={`h-4 w-4 ${checking ? "animate-spin" : ""}`} /> Check prices now</Button>
            </div>
          )}
        </div>

        {!user ? (
          <div className="mt-10 rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center">
            <Lock className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 font-semibold text-slate-700">Sign in to see your wishlist</p>
            <p className="text-sm text-slate-500">Save deals from Search or Explore and they’ll appear here.</p>
            <div className="mt-4 flex justify-center gap-2">
              <Link to="/login"><Button variant="outline">Login</Button></Link>
              <Link to="/register"><Button className="bg-blue-600 hover:bg-blue-700">Create account</Button></Link>
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center">
            <Heart className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 font-semibold text-slate-700">No saved deals yet</p>
            <p className="text-sm text-slate-500">Tap the heart on any deal to add it here.</p>
            <div className="mt-4 flex justify-center gap-2">
              <Link to="/search"><Button className="gap-2 bg-blue-600 hover:bg-blue-700"><Search className="h-4 w-4" /> Run a search</Button></Link>
              <Link to="/explore"><Button variant="outline">Explore deals</Button></Link>
            </div>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {items.map((d) => (
              <div key={d.savedId} data-testid="wishlist-item" className="flex flex-col gap-2">
                <FlightCard d={d} showFresh />
                <TargetPointsControl deal={d} onChange={refresh} />
                {daysFromToday(d.date) <= 1 && daysFromToday(d.date) >= -1 && (
                  <Link to={`/tracker?airport=${d.origin.code}&airline=${d.airlineCode}`} data-testid="track-flight-link" className="flex items-center justify-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100"><Radar className="h-3.5 w-3.5" /> Track this flight live</Link>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
